import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Zap,
  CreditCard,
  Globe2,
  Check,
  ShieldCheck,
  Layers,
  Clock
} from "lucide-react";
import SEO from "../components/SEO";

const PILLAR_ICONS = [Zap, CreditCard, Globe2];
const PILLAR_IDS = ["settlements", "backed-cards", "international"];
const TRUST_ICONS = [ShieldCheck, Layers, Globe2, Clock];

const StablecoinPage = () => {
  const { t } = useTranslation();
  const pillars = t("stablecoin.pillars.items", { returnObjects: true });
  const trust = t("stablecoin.trust", { returnObjects: true });
  const steps = t("stablecoin.flow.steps", { returnObjects: true });

  return (
    <div data-testid="stablecoin-page">
      <SEO
        path="/stablecoin"
        title={t("seo.stablecoin.title")}
        description={t("seo.stablecoin.description")}
        keywords={t("seo.stablecoin.keywords")}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Stablecoin Card Issuing & Settlement",
          "name": "Stablecoin cards and settlements",
          "provider": { "@type": "Organization", "name": t("brand.name") },
          "areaServed": ["Mexico", "Latin America"],
          "description": t("seo.stablecoin.description"),
          "offers": {
            "@type": "Offer",
            "category": "Stablecoin-backed Cards"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Stablecoin Card Capabilities",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Stablecoin settlements (USDC/USDT)" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Stablecoin-backed cards" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "International issuance with BIN sponsorship" } }
            ]
          }
        }}
      />
      {/* Hero */}
      <section className="hero" style={styles.hero}>
        <div className="container">
          <div className="heroInner" style={styles.heroInner}>
            <div className="eyebrow" style={styles.eyebrow}>{t("stablecoin.hero.eyebrow")}</div>
            <h1 className="heroTitle" style={styles.heroTitle}>
              {t("stablecoin.hero.titleLine1")}<br />{t("stablecoin.hero.titleLine2")}
            </h1>
            <p className="heroSubtitle" style={styles.heroSubtitle}>{t("stablecoin.hero.subtitle")}</p>
            <div className="heroCtaRow" style={styles.heroCtaRow}>
              <Link to="/contacto">
                <button className="btn-primary" data-testid="stablecoin-hero-cta">
                  {t("common.talkToExpert")}
                  <ArrowRight size={18} style={{ marginLeft: 8 }} />
                </button>
              </Link>
              <a
                href="#settlements"
                className="secondaryLink" style={styles.secondaryLink}
                data-testid="stablecoin-hero-scroll"
              >
                {t("common.viewHowItWorks")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="trustSection" style={styles.trustSection}>
        <div className="container">
          <div className="trustGrid" style={styles.trustGrid}>
            {trust.map((label, i) => {
              const Icon = TRUST_ICONS[i] || ShieldCheck;
              return (
                <div
                  key={i}
                  className="trustItem" style={styles.trustItem}
                  data-testid={`stablecoin-trust-${i}`}
                >
                  <Icon size={18} strokeWidth={1.75} color="#667eea" />
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="pillarsSection" style={styles.pillarsSection}>
        <div className="container">
          <div className="sectionHeader" style={styles.sectionHeader}>
            <h2 className="sectionTitle" style={styles.sectionTitle}>{t("stablecoin.pillars.title")}</h2>
            <p className="sectionSubtitle" style={styles.sectionSubtitle}>{t("stablecoin.pillars.subtitle")}</p>
          </div>

          <div className="pillarsList" style={styles.pillarsList}>
            {pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i] || Zap;
              const reverse = i % 2 === 1;
              const id = PILLAR_IDS[i] || `pillar-${i}`;
              return (
                <div
                  key={id}
                  id={id}
                  style={{
                    ...styles.pillarRow,
                    flexDirection: reverse ? "row-reverse" : "row"
                  }}
                  data-testid={`stablecoin-pillar-${id}`}
                >
                  <div className="pillarText" style={styles.pillarText}>
                    <div className="pillarEyebrow" style={styles.pillarEyebrow}>{p.eyebrow}</div>
                    <h3 className="pillarTitle" style={styles.pillarTitle}>{p.title}</h3>
                    <p className="pillarDescription" style={styles.pillarDescription}>{p.description}</p>
                    <ul className="bulletList" style={styles.bulletList}>
                      {p.bullets.map((b, j) => (
                        <li key={j} className="bulletItem" style={styles.bulletItem}>
                          <Check size={18} color="#f5576c" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="pillarVisual" style={styles.pillarVisual}>
                    <div className="pillarOrb" style={styles.pillarOrb}>
                      <Icon size={56} strokeWidth={1.5} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works (short flow) */}
      <section className="flowSection" style={styles.flowSection}>
        <div className="container">
          <div className="sectionHeader" style={styles.sectionHeader}>
            <h2 className="sectionTitleAlt" style={styles.sectionTitleAlt}>{t("stablecoin.flow.title")}</h2>
            <p className="sectionSubtitle" style={styles.sectionSubtitle}>{t("stablecoin.flow.subtitle")}</p>
          </div>

          <div className="flowGrid" style={styles.flowGrid}>
            {steps.map((s, i) => (
              <React.Fragment key={i}>
                <div className="flowStep" style={styles.flowStep} data-testid={`stablecoin-flow-${i + 1}`}>
                  <div className="flowNumber" style={styles.flowNumber}>{String(i + 1).padStart(2, "0")}</div>
                  <div className="flowTitle" style={styles.flowTitle}>{s.title}</div>
                  <div className="flowDesc" style={styles.flowDesc}>{s.desc}</div>
                </div>
                {i < steps.length - 1 && <div className="flowConnector" style={styles.flowConnector} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ctaSection" style={styles.ctaSection}>
        <div className="container">
          <div className="ctaInner" style={styles.ctaInner}>
            <h2 className="ctaTitle" style={styles.ctaTitle}>{t("stablecoin.cta.title")}</h2>
            <p className="ctaSubtitle" style={styles.ctaSubtitle}>{t("stablecoin.cta.subtitle")}</p>
            <Link to="/contacto">
              <button
                className="btn-primary ctaButton"
                style={styles.ctaButton}
                data-testid="stablecoin-final-cta"
              >
                {t("common.scheduleADemo")}
                <ArrowRight size={18} style={{ marginLeft: 8 }} />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  hero: {
    padding: "160px 20px 80px",
    background: "#ffffff"
  },
  heroInner: { maxWidth: "900px", margin: "0 auto" },
  eyebrow: {
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#f5576c",
    marginBottom: "20px"
  },
  heroTitle: {
    fontSize: "64px",
    fontWeight: 700,
    lineHeight: 1.05,
    letterSpacing: "-0.03em",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "24px"
  },
  heroSubtitle: {
    fontSize: "20px",
    lineHeight: 1.6,
    color: "#4a5568",
    maxWidth: "720px",
    marginBottom: "36px"
  },
  heroCtaRow: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
    flexWrap: "wrap"
  },
  secondaryLink: {
    color: "#667eea",
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "16px"
  },

  trustSection: {
    padding: "32px 20px",
    background: "#f8f9fc",
    borderTop: "1px solid rgba(0,0,0,0.06)",
    borderBottom: "1px solid rgba(0,0,0,0.06)"
  },
  trustGrid: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "40px",
    flexWrap: "wrap",
    maxWidth: "1100px",
    margin: "0 auto"
  },
  trustItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "15px",
    fontWeight: 500,
    color: "#2d3748"
  },

  pillarsSection: {
    padding: "100px 20px",
    background: "#ffffff"
  },
  sectionHeader: {
    textAlign: "center",
    maxWidth: "760px",
    margin: "0 auto 70px"
  },
  sectionTitle: {
    fontSize: "52px",
    fontWeight: 700,
    lineHeight: 1.1,
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "14px",
    letterSpacing: "-0.02em"
  },
  sectionTitleAlt: {
    fontSize: "52px",
    fontWeight: 700,
    lineHeight: 1.1,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "14px",
    letterSpacing: "-0.02em"
  },
  sectionSubtitle: {
    fontSize: "18px",
    color: "#4a5568",
    lineHeight: 1.6
  },
  pillarsList: {
    display: "flex",
    flexDirection: "column",
    gap: "80px",
    maxWidth: "1200px",
    margin: "0 auto"
  },
  pillarRow: {
    display: "flex",
    alignItems: "center",
    gap: "70px"
  },
  pillarText: {
    flex: 1.2,
    minWidth: 0
  },
  pillarEyebrow: {
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#f5576c",
    marginBottom: "12px"
  },
  pillarTitle: {
    fontSize: "40px",
    fontWeight: 700,
    color: "#1a202c",
    lineHeight: 1.15,
    letterSpacing: "-0.02em",
    marginBottom: "18px",
    whiteSpace: "pre-line"
  },
  pillarDescription: {
    fontSize: "17px",
    color: "#4a5568",
    lineHeight: 1.7,
    marginBottom: "22px"
  },
  bulletList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: "12px"
  },
  bulletItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    fontSize: "15px",
    color: "#2d3748",
    lineHeight: 1.55
  },
  pillarVisual: {
    flex: 1,
    display: "flex",
    justifyContent: "center"
  },
  pillarOrb: {
    width: "260px",
    height: "260px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle at 30% 30%, rgba(102,126,234,0.18), rgba(118,75,162,0.10) 50%, transparent 70%), linear-gradient(180deg, #ffffff 0%, #f8f9fc 100%)",
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 30px 60px -20px rgba(102, 126, 234, 0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#667eea"
  },

  flowSection: {
    padding: "100px 20px",
    background: "#f8f9fc",
    borderTop: "1px solid rgba(0,0,0,0.06)"
  },
  flowGrid: {
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
    gap: "16px",
    maxWidth: "1100px",
    margin: "0 auto"
  },
  flowStep: {
    flex: 1,
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: "16px",
    padding: "28px",
    minWidth: 0
  },
  flowConnector: {
    width: "40px",
    height: "2px",
    alignSelf: "center",
    background:
      "linear-gradient(90deg, rgba(102,126,234,0.6) 0%, rgba(245,87,108,0.6) 100%)",
    borderRadius: "2px"
  },
  flowNumber: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#f5576c",
    letterSpacing: "0.08em",
    marginBottom: "10px"
  },
  flowTitle: {
    fontSize: "19px",
    fontWeight: 700,
    color: "#1a202c",
    letterSpacing: "-0.01em",
    marginBottom: "8px",
    lineHeight: 1.3
  },
  flowDesc: {
    fontSize: "15px",
    color: "#4a5568",
    lineHeight: 1.6
  },

  ctaSection: {
    padding: "100px 20px",
    background: "#ffffff",
    borderTop: "1px solid rgba(0,0,0,0.08)"
  },
  ctaInner: {
    maxWidth: "700px",
    margin: "0 auto",
    textAlign: "center"
  },
  ctaTitle: {
    fontSize: "52px",
    fontWeight: 700,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "20px",
    letterSpacing: "-0.02em"
  },
  ctaSubtitle: {
    fontSize: "19px",
    color: "#4a5568",
    lineHeight: 1.6,
    marginBottom: "36px"
  },
  ctaButton: {
    fontSize: "17px",
    padding: "16px 36px"
  }
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @media (max-width: 1024px) {
    [data-testid="stablecoin-page"] h1 { font-size: 44px !important; }
    [data-testid="stablecoin-page"] h2 { font-size: 38px !important; }
    [data-testid^="stablecoin-pillar-"] { flex-direction: column !important; gap: 40px !important; text-align: left; }
    [data-testid^="stablecoin-pillar-"] > div:last-child { width: 100%; }
    [data-testid="stablecoin-page"] div[style*="flex: 1"][style*="padding: 28px"] { flex: none !important; width: 100% !important; }
    [data-testid="stablecoin-page"] div.flow[style*="display: flex"] { flex-direction: column !important; }
  }
  @media (max-width: 640px) {
    [data-testid="stablecoin-page"] section:first-child { padding: 120px 16px 60px !important; }
    [data-testid="stablecoin-page"] h1 { font-size: 34px !important; }
    [data-testid="stablecoin-page"] h2 { font-size: 28px !important; }
    [data-testid="stablecoin-page"] h3 { font-size: 28px !important; }
    [data-testid="stablecoin-page"] div.trustGrid,
    [data-testid="stablecoin-page"] section[style*="padding: 32px"] > div > div { gap: 18px !important; }
    [data-testid="stablecoin-page"] div[style*="width: 260px"][style*="height: 260px"] {
      width: 200px !important; height: 200px !important;
    }
    [data-testid="stablecoin-page"] div.flowGrid,
    [data-testid="stablecoin-page"] div[style*="display: flex"][style*="gap: 16px"] {
      flex-direction: column !important;
    }
    [data-testid="stablecoin-page"] div[style*="width: 40px"][style*="height: 2px"] {
      width: 2px !important; height: 24px !important;
      background: linear-gradient(180deg, rgba(102,126,234,0.6) 0%, rgba(245,87,108,0.6) 100%) !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default StablecoinPage;
